import ky, { BeforeRequestHook } from 'ky';
import {
  DeviceAnalysisResult,
  PGNFields,
  PGNParseResult,
  ResponseMessage,
  ResponseWithMessages,
  Shape2IsoXmlFields,
  ClientNameFields,
  ClientNameParseResult,
  ISOXMLCheckFields,
  ISOXMLFinisherFields,
} from './dto';
import { buildFormData, downloadFileFromResponse } from './utils';
import { AuthLocalStorageKeys } from '../auth-config/auth-service';

const authHook: BeforeRequestHook = (request) => {
  const token = localStorage.getItem(AuthLocalStorageKeys.AUTH_TOKEN)
  const header = request.headers.get("Authorization")
  if (!header?.startsWith("Basic") && token) request.headers.append("Authorization", `Bearer ${token}`)
};

const server = ky.create({
  prefixUrl: process.env.REACT_APP_DEV4AG_TOOLS_URL,
  throwHttpErrors: false,
  hooks: { beforeRequest: [authHook] }
});

export class ResponseErrorWithMessages extends Error {
  responseMessages: ResponseMessage[];
  constructor(message: string, responseMessages: ResponseMessage[]) {
    super(message);
    this.responseMessages = responseMessages;
  }
}

async function processServerResponse<OutputType>(
  response: Response
): Promise<ResponseWithMessages<OutputType>> {
  let json: ResponseWithMessages<OutputType>;
  try {
    json = (await response.json()) as ResponseWithMessages<OutputType>;
  } catch {
    const msg: ResponseMessage = {
      type: "Error",
      code: "response-parse-error",
      title: "Server Error",
      details: {
        responseCode: response.status,
      },
      description: "Can't parse server-side reponse"
    };
    throw new ResponseErrorWithMessages(
      `Can't parse server-side reponse (status code: ${response.status})`,
      [msg]
    );
  }
  if (response.ok) {
    return json;
  } else if (response.status === 400) {
    throw new ResponseErrorWithMessages("Validation error", json.messages);
  } else {
    throw new ResponseErrorWithMessages(
      `Server-side error (status code: ${response.status})`,
      [
        {
          type: "Error",
          code: "server-error",
          title: "Server-side error",
          details: {
            responseCode: response.status,
          },
          description: "Server-Side Error"
        },
      ]
    );
  }
}

async function baseRequestFunction<OutputType>(
  url: string,
  params: any
): Promise<ResponseWithMessages<OutputType>> {
  let response: Response;
  try {
    response = await server.get(url, {
      searchParams: params,
    });
  } catch (err: any) {
    const msg = err instanceof Error ? err.message : (err as string);
    throw new ResponseErrorWithMessages(err, [
      {
        type: "Error",
        code: "server-unavailable",
        title: "Server unavailable",
        description : msg
      },
    ]);
  }
  return processServerResponse(response);
}

export function parsePGN(pgn: string) {
  return baseRequestFunction<PGNParseResult>("pgn/parse", { pgn });
}

export async function buildPGN(pgnFields: PGNFields) {
  return baseRequestFunction<PGNParseResult>("pgn/build", pgnFields);
}

export function parseClientName(clientNameString: string) {
  return baseRequestFunction<ClientNameParseResult>("clientName/parse", { clientNameString });
}

export async function buildClientName(clientNameFields: ClientNameFields) {
  return baseRequestFunction<ClientNameParseResult>("clientName/build", clientNameFields);
}


export async function analyzeDevice(
  xmlDeviceDescription: string,
  includeElements: boolean = false,
  includeDevice: boolean = false,
  includeHierarchy: boolean = true
) {
  const formData = new FormData();
  formData.append("deviceXML", xmlDeviceDescription);

  const response = await server.post(
    `device/analyze?includeElements=${includeElements}&includeDevice=${includeDevice}&includeHierarchy=${includeHierarchy}`,
    {
      body: formData,
    }
  );

  return processServerResponse<DeviceAnalysisResult>(response);
}

export async function convertDeviceToDDOP(
  xmlDeviceDescription:string,
  version:number
){
  const formData = new FormData();
  formData.append("deviceXML", xmlDeviceDescription);
  const response = await server.post(
    `device/convertToBin?version=`+version,{
      body:formData
    }
  )
  if (!response.ok) {
    if(response.body!==undefined){
      return await response.json()
    } else {
      return {messages:[
        { 
          code: "100",
          title: 'No response received',
          type:'Error',
          description:'No response received'
        }
      ]}
    }
  }

  await downloadFileFromResponse(response)

  return;
}


export async function shareData(data: string, comment?: string) {
  const formData = new FormData();
  formData.append("data", data);
  if (comment) {
    formData.append("comment", comment);
  }

  const response = await server.post("sharedData", {
    body: formData,
  });

  return processServerResponse<never>(response);
}

export async function transformShapeToIsoXml(payload: Shape2IsoXmlFields):Promise<any> {
  try {
    const formData = new FormData();
    const config = {
      ...payload.config,
      input: {
        ...payload.config.input,
        matchings: payload.config.input?.matchings?.map((matching) => {
          const { taskNameFromFile, unitName, ...restMatching } = matching;
          return {
            ...restMatching,
            ...(unitName && unitName.trim() !== "" ? { unitName: unitName.trim() } : {}),
            taskNameFromFilename: taskNameFromFile,
          };
        }),
      },
    };

    formData.append("config", JSON.stringify(config));
    if (payload.geometry_source) {
      formData.append("geometry_source", payload.geometry_source);
    }
    if (payload.isoxmlTemplate) {
      formData.append("isoxml_template", payload.isoxmlTemplate);
    }

    const response = await server.post("shp2isoxml", {
      body: formData,
      timeout: 100000
    });

    if (!response.ok) {
      if (response.body !== undefined) {
        try {
          const errorResponse = await response.json() as { message?: string; code?: string };
          return { message: errorResponse.message || `Request failed with HTTP ${response.status}` };
        } catch {
          return { message: `Request failed with HTTP ${response.status}` };
        }
      }
      return { message: 'No response received' };
    }

    await downloadFileFromResponse(response)

    return;
  } catch (_) {
    return { message: 'Unexpected error happened, please try again!' }
  }
}


export async function checkISOXMLTaskSet(payload: ISOXMLCheckFields): Promise<ResponseWithMessages<void>> {
  try {
    const formData = new FormData()
    buildFormData(formData, payload)
    const response = await server.post("isoxml/check", {
      body: formData,
      timeout: 100000
    });
    if( response){
      return await response.json()      
    } else {
      return { messages: [{ 
        code: "100",
        title: 'No response received',
        type:'Error',
        description:'No response received'
      }] };
    }
  } catch (_) {
    return { messages: [{ 
      code: "100",
      title: 'Unexpected Error',
      type:'Error',
      description:'Unexpected error happened, please try again!'
    }]  }
  }
}

// New 3-endpoint pattern for ISOXML Check and Download
export async function startISOXMLCheckJob(payload: ISOXMLCheckFields): Promise<{ jobId: string } | { message: string }> {
  try {
    const formData = new FormData()
    buildFormData(formData, payload)
    const response = await server.post("isoxml/check", {
      body: formData
    })
    
    if (response.ok) {
      const result = await response.json() as { jobId: string }
      return { jobId: result.jobId }
    } else {
      const error = await response.json() as { message?: string }
      return { message: error.message || 'Failed to start check job' }
    }
  } catch (error) {
    console.error('Error starting ISOXML check job:', error);
    return { message: 'Unexpected error happened, please try again!' }
  }
}

export async function getISOXMLCheckJobStatus(jobId: string): Promise<{ status: string; result?: any; errorMessage?: string } | { message: string }> {
  try {
    const response = await server.get(`isoxml/check/${jobId}`)
    
    if (response.ok) {
      const result = await response.json() as { status: string; result?: any; errorMessage?: string }
      return {
        status: result.status,
        result: result.result,
        errorMessage: result.errorMessage
      }
    } else {
      return { message: 'Failed to get check job status' }
    }
  } catch (error) {
    console.error('Error getting check job status:', error);
    return { message: 'Unexpected error happened, please try again!' }
  }
}

export async function startISOXMLToResultZipJob(payload: ISOXMLCheckFields): Promise<{ jobId: string } | { message: string }> {
  try {
    const formData = new FormData()
    buildFormData(formData, payload)
    const response = await server.post("isoxml/to-result-zip", {
      body: formData
    })
    
    if (response.ok) {
      const result = await response.json() as { jobId: string }
      return { jobId: result.jobId }
    } else {
      const error = await response.json() as { message?: string }
      return { message: error.message || 'Failed to start conversion job' }
    }
  } catch (error) {
    console.error('Error starting ISOXML conversion job:', error);
    return { message: 'Unexpected error happened, please try again!' }
  }
}

export async function getISOXMLToResultZipJobStatus(jobId: string): Promise<{ status: string; result?: any; errorMessage?: string } | { message: string }> {
  try {
    const response = await server.get(`isoxml/to-result-zip/${jobId}`)
    
    if (response.ok) {
      const result = await response.json() as { status: string; result?: any; errorMessage?: string }
      return {
        status: result.status,
        result: result.result,
        errorMessage: result.errorMessage
      }
    } else {
      return { message: 'Failed to get conversion job status' }
    }
  } catch (error) {
    console.error('Error getting conversion job status:', error);
    return { message: 'Unexpected error happened, please try again!' }
  }
}

export async function downloadISOXMLToResultZip(jobId: string): Promise<void> {
  try {
    const response = await server.get(`isoxml/to-result-zip/${jobId}/download`)
    
    if (response.ok) {
      await downloadFileFromResponse(response)
    } else {
      throw new Error('Failed to download result')
    }
  } catch (error) {
    console.error('Error downloading result:', error);
    throw error
  }
}

// Legacy function for backward compatibility - now uses new pattern
export async function convertResultISOXMLTaskSet(payload: ISOXMLCheckFields, signal?: AbortSignal):Promise<any> {
  try {
    console.log('Starting convertResultISOXMLTaskSet with signal:', signal?.aborted ? 'aborted' : 'active');
    
    // Check if signal is already aborted
    if (signal?.aborted) {
      throw new Error('Request was cancelled before starting');
    }

    // Start the job
    const jobResult = await startISOXMLToResultZipJob(payload);
    
    if ('message' in jobResult) {
      return { message: jobResult.message };
    }

    // Poll for completion
    let attempts = 0;
    const maxAttempts = 150; // 5 minutes with 2-second intervals
    
    while (attempts < maxAttempts) {
      // Check if cancelled
      if (signal?.aborted) {
        throw new Error('Request was cancelled by user');
      }

      const statusResult = await getISOXMLToResultZipJobStatus(jobResult.jobId);
      
      if ('message' in statusResult) {
        return { message: statusResult.message };
      }

      const { status, errorMessage } = statusResult;
      
      if (status === 'Completed') {
        // Download the result
        await downloadISOXMLToResultZip(jobResult.jobId);
        return; // Success - file downloaded
      } else if (status === 'Failed') {
        return { message: errorMessage || 'Job failed' };
      }
      
      // Wait 2 seconds before next poll
      await new Promise(resolve => setTimeout(resolve, 2000));
      attempts++;
    }
    
    return { message: 'Job timed out after 5 minutes' };
    
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError' || error.message.includes('cancelled')) {
        return { message: 'Operation cancelled by user' }
      }
      if (error.message.includes('NS_BINDING_ABORTED') || error.message.includes('aborted')) {
        return { message: 'Operation cancelled by user' }
      }
    }
    console.error('Error in convertResultISOXMLTaskSet:', error);
    return { message: 'Unexpected error happened, please try again!' }
  }
}


// New 3-endpoint pattern for ISOXML Finisher
export async function startISOXMLFinishJob(payload: ISOXMLFinisherFields): Promise<{ jobId: string } | { message: string }> {
  try {
    const formData = new FormData()
    buildFormData(formData, payload)
    const response = await server.post("isoxml/finish", {
      body: formData
    })
    
    if (response.ok) {
      const result = await response.json() as { jobId: string }
      return { jobId: result.jobId }
    } else {
      const error = await response.json() as { message?: string }
      return { message: error.message || 'Failed to start job' }
    }
  } catch (error) {
    console.error('Error starting ISOXML finish job:', error);
    return { message: 'Unexpected error happened, please try again!' }
  }
}

export async function getISOXMLFinishJobStatus(jobId: string): Promise<{ status: string; result?: any; errorMessage?: string } | { message: string }> {
  try {
    const response = await server.get(`isoxml/finish/${jobId}`)
    
    if (response.ok) {
      const result = await response.json() as { status: string; result?: any; errorMessage?: string }
      return {
        status: result.status,
        result: result.result,
        errorMessage: result.errorMessage
      }
    } else {
      return { message: 'Failed to get job status' }
    }
  } catch (error) {
    console.error('Error getting job status:', error);
    return { message: 'Unexpected error happened, please try again!' }
  }
}

export async function downloadISOXMLFinishResult(jobId: string): Promise<void> {
  try {
    const response = await server.get(`isoxml/finish/${jobId}/download`)
    
    if (response.ok) {
      await downloadFileFromResponse(response)
    } else {
      throw new Error('Failed to download result')
    }
  } catch (error) {
    console.error('Error downloading result:', error);
    throw error
  }
}

// Legacy function for backward compatibility
export async function finishISOXMLTaskSet(payload: ISOXMLFinisherFields){
  try {
    const formData = new FormData()
    buildFormData(formData,payload)
    const response = await server.post("isoxml/finish",{
      body: formData
    })
    if( response.ok){
      await downloadFileFromResponse(response)
    }
    return;
  } catch (_) {
    return { message: 'Unexpected error happened, please try again!' }
  }
}